import { z } from 'zod';

/** Static binding refs (non-slugged). */
export const STATIC_FIELD_BINDING_REFS = [
  'pdl.legalName',
  'pdl.representative',
  'pdl.legalAddress',
  'pdl.legalTelephone',
  'pdl.legalContactEmail',
  'pdl.registerCourt',
  'pdl.registerNumber',
  'pdl.vatId',
  'pdl.openingHours',
  'pdl.socialLinks',
  'config.businessName',
] as const;

const STATIC_FIELD_BINDING_REF_SET = new Set<string>(STATIC_FIELD_BINDING_REFS);

/** Slugged contact / store refs: `pdl.telephone-support`, `pdl.storeAddress-mitte`, … */
export const SLUGGED_FIELD_BINDING_REF_PATTERN =
  /^pdl\.(telephone|contactEmail|storeAddress)-[a-z0-9]+$/;

/** Legacy indexed refs still accepted when reading older drafts. */
const LEGACY_INDEXED_BINDING_REF_PATTERN =
  /^pdl\.(telephone|contactEmail|businessAddress|address)([1-5])?$/;

function isFieldBindingRefString(value: string): boolean {
  if (STATIC_FIELD_BINDING_REF_SET.has(value)) return true;
  if (SLUGGED_FIELD_BINDING_REF_PATTERN.test(value)) return true;
  if (LEGACY_INDEXED_BINDING_REF_PATTERN.test(value)) return true;
  return false;
}

/** Binding refs — PDL paths (incl. description slugs) or project configuration. */
export const FieldBindingRefSchema = z.string().min(1).refine(isFieldBindingRefString, {
  message: 'Invalid field binding ref',
});

export type FieldBindingRef = z.infer<typeof FieldBindingRefSchema>;

/**
 * Per block-field role → source ref.
 * Role keys match content field keys (e.g. legalName, controllerName, phone).
 *
 * Semantics:
 * - `fieldBindings` omitted/null → legacy defaults (all known roles bound).
 * - `fieldBindings` present → only listed roles are bound; missing role = unbound (CDL).
 */
export const FieldBindingsSchema = z.record(z.string().min(1), FieldBindingRefSchema);

export type FieldBindings = z.infer<typeof FieldBindingsSchema>;

/** How a role’s allow-list is populated. */
export type FieldBindingRoleKind = 'static' | 'telephone' | 'email' | 'address';

export const FIELD_BINDING_ROLE_KIND: Record<string, FieldBindingRoleKind> = {
  legalName: 'static',
  controllerName: 'static',
  representative: 'static',
  registerCourt: 'static',
  registerNumber: 'static',
  vatId: 'static',
  hours: 'static',
  socialLinks: 'static',
  contactPhone: 'telephone',
  phone: 'telephone',
  contactEmail: 'email',
  email: 'email',
  address: 'address',
  controllerAddress: 'address',
};

/**
 * Static allow-lists (name / registry).
 * Telephone / email / address options are built from live PDL descriptions
 * (`@telephone-support`, `@storeAddress-mitte`, …) — see `bindingOptionsForRole`.
 */
export const FIELD_BINDING_ALLOWLIST: Record<string, readonly FieldBindingRef[]> = {
  legalName: ['pdl.legalName', 'config.businessName'],
  controllerName: ['pdl.legalName', 'config.businessName'],
  representative: ['pdl.representative'],
  registerCourt: ['pdl.registerCourt'],
  registerNumber: ['pdl.registerNumber'],
  vatId: ['pdl.vatId'],
  hours: ['pdl.openingHours'],
  socialLinks: ['pdl.socialLinks'],
  /** Options built from Persona values — see `bindingOptionsForRole`. */
  address: [],
  controllerAddress: [],
  contactPhone: [],
  phone: [],
  contactEmail: [],
  email: [],
};

/** Default binding when `fieldBindings` map is entirely absent (legacy). */
export const DEFAULT_FIELD_BINDING_BY_ROLE: Record<string, FieldBindingRef> = {
  legalName: 'pdl.legalName',
  representative: 'pdl.representative',
  /** Footer / guest — primary store (slug `primary` if no description). */
  address: 'pdl.storeAddress-primary',
  contactPhone: 'pdl.telephone-primary',
  contactEmail: 'pdl.contactEmail-primary',
  registerCourt: 'pdl.registerCourt',
  registerNumber: 'pdl.registerNumber',
  vatId: 'pdl.vatId',
  controllerName: 'pdl.legalName',
  controllerAddress: 'pdl.legalAddress',
  phone: 'pdl.telephone-primary',
  email: 'pdl.contactEmail-primary',
  hours: 'pdl.openingHours',
  socialLinks: 'pdl.socialLinks',
};

/** Whether `ref` is a valid choice for `role` (schema + role kind), ignoring live PDL. */
export function isFieldBindingRefCompatible(role: string, ref: string): boolean {
  if (!isFieldBindingRefString(ref)) return false;
  const kind = FIELD_BINDING_ROLE_KIND[role];
  if (!kind) return false;
  if (kind === 'static') {
    return (FIELD_BINDING_ALLOWLIST[role] ?? []).includes(ref as FieldBindingRef);
  }
  if (kind === 'telephone') {
    return (
      ref === 'pdl.legalTelephone' ||
      /^pdl\.telephone-[a-z0-9]+$/.test(ref) ||
      /^pdl\.telephone[1-5]$/.test(ref) ||
      ref === 'pdl.telephone'
    );
  }
  if (kind === 'email') {
    return (
      ref === 'pdl.legalContactEmail' ||
      /^pdl\.contactEmail-[a-z0-9]+$/.test(ref) ||
      /^pdl\.contactEmail[1-5]$/.test(ref) ||
      ref === 'pdl.contactEmail'
    );
  }
  // address
  return (
    ref === 'pdl.legalAddress' ||
    /^pdl\.storeAddress-[a-z0-9]+$/.test(ref) ||
    /^pdl\.businessAddress[1-5]?$/.test(ref) ||
    ref === 'pdl.address'
  );
}

/** Fixed prose tokens (+ dynamic slug tokens accepted via `isLegalProseTokenKey`). */
export const LEGAL_PROSE_TOKEN_KEYS = [
  'legalName',
  'businessName',
  'representative',
  'address',
  'legalAddress',
  'legalTelephone',
  'legalContactEmail',
  'contactPhone',
  'contactEmail',
  'registerCourt',
  'registerNumber',
  'vatId',
  'hours',
] as const;

export type LegalProseTokenKey = (typeof LEGAL_PROSE_TOKEN_KEYS)[number];

const LEGAL_PROSE_TOKEN_SET = new Set<string>(LEGAL_PROSE_TOKEN_KEYS);

const DYNAMIC_PROSE_TOKEN_PATTERN =
  /^(telephone|contactEmail|storeAddress)-[a-z0-9]+$/;

export function isLegalProseTokenKey(key: string): boolean {
  return LEGAL_PROSE_TOKEN_SET.has(key) || DYNAMIC_PROSE_TOKEN_PATTERN.test(key);
}

export function defaultBindingForRole(role: string): FieldBindingRef | null {
  return DEFAULT_FIELD_BINDING_BY_ROLE[role] ?? null;
}

/**
 * Resolve binding for a role.
 * - No map → legacy defaults.
 * - Explicit map → listed ref, or `null` when role omitted (unbound).
 */
export function resolveBindingForRole(
  fieldBindings: FieldBindings | null | undefined,
  role: string,
): FieldBindingRef | null {
  if (fieldBindings == null) {
    return defaultBindingForRole(role);
  }
  const explicit = fieldBindings[role];
  if (!explicit) {
    return null;
  }
  if (!isFieldBindingRefCompatible(role, explicit)) {
    return defaultBindingForRole(role);
  }
  return explicit;
}

export function isFieldRoleBound(
  fieldBindings: FieldBindings | null | undefined,
  role: string,
): boolean {
  return resolveBindingForRole(fieldBindings, role) != null;
}

/** Placeholder shown when an unbound value field is empty (`@legalName`, …). */
export function bindingPlaceholderToken(role: string): string {
  switch (role) {
    case 'controllerName':
      return '@legalName';
    case 'controllerAddress':
    case 'address':
      return '@legalAddress';
    case 'phone':
    case 'contactPhone':
      return '@legalTelephone';
    case 'email':
    case 'contactEmail':
      return '@legalContactEmail';
    case 'hours':
      return '@hours';
    default:
      return `@${role}`;
  }
}

/** Canvas / dropdown display for a binding ref (`pdl.legalName` → `@legalName`). */
export function bindingRefDisplayToken(ref: string): string {
  return ref.replace(/^(pdl|config)\./, '@');
}

/** Imprint PDL roles — keep in sync with LEGAL_IMPRINT_PDL_FIELD_KEYS. */
const IMPRINT_BINDABLE_ROLES = [
  'legalName',
  'representative',
  'address',
  'contactPhone',
  'contactEmail',
  'registerCourt',
  'registerNumber',
  'vatId',
] as const;

/** Privacy PDL roles — keep in sync with LEGAL_PRIVACY_PDL_FIELD_KEYS. */
const PRIVACY_BINDABLE_ROLES = [
  'controllerName',
  'controllerAddress',
  'contactEmail',
] as const;

/** Seed explicit bindings for imprint / privacy PDL roles (new blocks). */
export function defaultLegalFieldBindings(
  kind: 'imprint' | 'privacy',
): FieldBindings {
  const roles = kind === 'imprint' ? IMPRINT_BINDABLE_ROLES : PRIVACY_BINDABLE_ROLES;
  const out: FieldBindings = {};
  for (const role of roles) {
    const ref = defaultBindingForRole(role);
    if (ref) {
      out[role] = ref;
    }
  }
  if (kind === 'imprint') {
    out.address = 'pdl.legalAddress';
    out.contactPhone = 'pdl.legalTelephone';
    out.contactEmail = 'pdl.legalContactEmail';
  }
  if (kind === 'privacy') {
    out.contactEmail = 'pdl.legalContactEmail';
  }
  return out;
}

/**
 * Materialize legacy defaults into an explicit map so one role can be unbound
 * without clearing sibling defaults.
 */
export function materializeFieldBindings(
  fieldBindings: FieldBindings | null | undefined,
  roles: readonly string[],
): FieldBindings {
  if (fieldBindings != null) {
    return { ...fieldBindings };
  }
  const out: FieldBindings = {};
  for (const role of roles) {
    const ref = defaultBindingForRole(role);
    if (ref) {
      out[role] = ref;
    }
  }
  return out;
}

/**
 * Imprint + privacy PDL roles that *may* open the Legal drawer (not Google & AI).
 * Final routing also depends on the active binding ref — see `opensLegalDrawer`.
 */
export const LEGAL_DRAWER_FIELD_KEYS = new Set<string>([
  'legalName',
  'representative',
  'address',
  'contactPhone',
  'contactEmail',
  'registerCourt',
  'registerNumber',
  'vatId',
  'controllerName',
  'controllerAddress',
]);

/**
 * Whether the pencil / persona CTA should open the Imprint (Legal) drawer.
 *
 * - `legalName` / `controllerName` → Legal when bound to `pdl.legalName`;
 *   Google & AI when bound to `config.businessName` (store/place name).
 * - `address` / `controllerAddress` → Legal when bound to `pdl.legalAddress`;
 *   Google & AI when bound to a store address.
 * - Footer guest fields always open Google & AI.
 */
export function opensLegalDrawer(
  fieldKey: string,
  componentType?: string,
  bindingRef?: FieldBindingRef | null,
): boolean {
  if (
    componentType === 'Footer_Standard' &&
    (fieldKey === 'address' ||
      fieldKey === 'phone' ||
      fieldKey === 'email' ||
      fieldKey === 'hours')
  ) {
    return false;
  }

  const ref = bindingRef ?? defaultBindingForRole(fieldKey);

  if (fieldKey === 'legalName' || fieldKey === 'controllerName') {
    return ref !== 'config.businessName';
  }

  if (fieldKey === 'address' || fieldKey === 'controllerAddress') {
    if (ref === 'pdl.legalAddress') return true;
    if (
      ref === 'pdl.address' ||
      (typeof ref === 'string' && ref.startsWith('pdl.storeAddress-')) ||
      (typeof ref === 'string' && /^pdl\.businessAddress/.test(ref))
    ) {
      return false;
    }
    // Imprint default / unknown → Legal
    return componentType !== 'Footer_Standard';
  }

  if (fieldKey === 'contactPhone' || fieldKey === 'phone') {
    if (ref === 'pdl.legalTelephone') return true;
    if (
      typeof ref === 'string' &&
      (ref.startsWith('pdl.telephone-') || /^pdl\.telephone[1-5]?$/.test(ref))
    ) {
      return false;
    }
    return LEGAL_DRAWER_FIELD_KEYS.has(fieldKey);
  }

  if (fieldKey === 'contactEmail' || fieldKey === 'email') {
    if (ref === 'pdl.legalContactEmail') return true;
    if (
      typeof ref === 'string' &&
      (ref.startsWith('pdl.contactEmail-') || /^pdl\.contactEmail[1-5]?$/.test(ref))
    ) {
      return false;
    }
    return LEGAL_DRAWER_FIELD_KEYS.has(fieldKey);
  }

  return LEGAL_DRAWER_FIELD_KEYS.has(fieldKey);
}
